import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UseFormReturn,
  SubmitHandler,
  SubmitErrorHandler,
} from 'react-hook-form';
import _ from 'lodash';
import {
  useSendFollowUpToManyMutation,
  FollowUpTemplateDataFragment,
  PatientDataFragment,
  PatientNameDataFragment,
  usePatientLazyQuery,
  SignatureUser,
} from '../../../../generated/graphql';
import {
  FollowUpForm,
  FollowUpDecoratorKeys,
  SavedActivityInputPreview,
} from '../../../lib/followUp';
import { customToast } from '../../../components/ToastAlert/customToast';
import { getProgramAssessmentIdsNeedingSignature } from '../helpers';
import { useAuth } from '../../../../contexts/AuthContext';
import { logger } from '../../../../lib/logger';
import { PatientSelectionEntry } from './useRecipientSelection';
import { SavedActivityInputPreviewWithActivityIndex } from '../../../components/Modals/MultiSignatureModals/ReviewSignerDetailsModal';
import { MULTI_FORM_FETCH_DELAY_MS } from '../../../lib/constants';

interface UseFollowUpSubmissionProps {
  form: UseFormReturn<FollowUpForm>;
  currentFollowUpTemplateDraftId: React.MutableRefObject<string | undefined>;
  followUpDrafts: FollowUpTemplateDataFragment[];
  patientSelections: Record<string, PatientSelectionEntry>;
  tagOrder: string[];
  isIntake: boolean;
  patientsData: any; // Using any for now as the type from usePatientsV2NamesQuery is complex
}

export const useFollowUpSubmission = ({
  form,
  currentFollowUpTemplateDraftId,
  followUpDrafts,
  patientSelections,
  tagOrder,
  isIntake,
  patientsData,
}: UseFollowUpSubmissionProps) => {
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [
    isSendToTagConfirmationModalOpen,
    setIsSendToTagConfirmationModalOpen,
  ] = useState(false);
  const [recipientPatientNames, setRecipientPatientNames] = useState<
    PatientNameDataFragment[]
  >([]);

  const [isReviewSignerDetailsModalOpen, setIsReviewSignerDetailsModalOpen] =
    useState(false);
  const [
    unassignedMultiSignatureActivities,
    setUnassignedMultiSignatureActivities,
  ] = useState<SavedActivityInputPreviewWithActivityIndex[]>();
  const [patientsDataForModal, setPatientsDataForModal] = useState<
    PatientDataFragment[]
  >([]);

  const [fetchPatient] = usePatientLazyQuery();
  const [sendFollowUpToManyMutation, { loading: sendingFollowUp }] =
    useSendFollowUpToManyMutation();

  const { getValues, handleSubmit, watch } = form;

  const watchPatientsOrTags = watch('patientsOrTags');
  const onlyPatientOrTag =
    watchPatientsOrTags.length === 1 && watchPatientsOrTags?.[0];
  const onlyPatient =
    onlyPatientOrTag?.__typename === 'PatientName' && onlyPatientOrTag;

  const isMultiSignatureActivitiesValid = (
    activities: SavedActivityInputPreview[],
  ) => {
    const multiSignatureActivities = activities
      .map((activity, activityIndex) => ({ ...activity, activityIndex }))
      .filter((activity) => activity.multiSignatureAssessmentConfig);

    if (multiSignatureActivities.length === 0) {
      return true;
    }

    setSubmitting(false);

    const unassignedMultiSignatureActivities = multiSignatureActivities.filter(
      (activity) =>
        !activity.sendFollowUpActivityInputRaw?.programAssessmentSignatureUsers,
    );

    if (unassignedMultiSignatureActivities.length > 0) {
      setUnassignedMultiSignatureActivities(unassignedMultiSignatureActivities);

      // Collect all patient names from both direct selections and tag selections
      // Note: This logic is simplified here as we don't have access to tagSelections directly
      // Ideally we should pass the resolved list of patients here

      // For now, we'll just use the selected patients from patientSelections which should be populated
      const allPatientNames: PatientNameDataFragment[] = Object.values(
        patientSelections,
      )
        .filter((entry) => entry.manual || entry.selectedTagIds.length > 0)
        .map((entry) => entry.patient);

      // Fetch patient data for all patients (single or multiple) to get assignedProviderUsers
      // IMPORTANT: Fetch sequentially with delays to avoid Apollo race condition where concurrent
      // requests with the same operation name but different variables get mixed up

      const fetchPatientsSequentially = async (): Promise<
        PatientDataFragment[]
      > => {
        const patients: PatientDataFragment[] = [];

        for (let index = 0; index < allPatientNames.length; index++) {
          const patientName = allPatientNames[index];

          // Add delay between requests to prevent Apollo from mixing up concurrent requests
          if (index > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, MULTI_FORM_FETCH_DELAY_MS),
            );
          }

          try {
            const result = await fetchPatient({
              variables: { programId: patientName.programInstanceId },
              fetchPolicy: 'network-only', // Force network request to bypass cache and get fresh data
            });

            const returnedPatient = result.data?.patient;

            if (
              returnedPatient &&
              returnedPatient.programInstanceId !==
                patientName.programInstanceId
            ) {
              logger.error(
                new Error('Patient data mismatch in fetchPatientsSequentially'),
                {
                  requestedProgramId: patientName.programInstanceId,
                  requestedName: `${patientName.firstName} ${patientName.lastName}`,
                  receivedProgramId: returnedPatient.programInstanceId,
                  receivedName: `${returnedPatient.firstName} ${returnedPatient.lastName}`,
                },
              );
              // Skip mismatched patients
              continue;
            }

            if (returnedPatient) {
              patients.push(returnedPatient);
            }
          } catch (error) {
            logger.error(
              error instanceof Error
                ? error
                : new Error(
                    'Error fetching patient in fetchPatientsSequentially',
                  ),
              {
                programId: patientName.programInstanceId,
                patientName: `${patientName.firstName} ${patientName.lastName}`,
              },
            );
            // Continue fetching other patients even if one fails
          }
        }

        return patients;
      };

      fetchPatientsSequentially()
        .then((patients) => {
          setPatientsDataForModal(patients);
          setIsReviewSignerDetailsModalOpen(true);
        })
        .catch(() => {
          customToast.error('Unable to fetch client data.');
        });
      return false;
    }

    return true;
  };

  const onSendFollowUp: SubmitHandler<FollowUpForm> = async () => {
    setSubmitting(true);

    try {
      const {
        subject,
        note: providerNote,
        providerVoiceNoteId,
        activities,
      } = getValues();

      if (!isMultiSignatureActivitiesValid(activities)) return;

      const multiSignatureActivities = activities.filter(
        (activity) => activity.multiSignatureAssessmentConfig,
      );

      const hasProviderFirstSigner = multiSignatureActivities.some(
        (activity) => {
          const firstSigner =
            activity.multiSignatureAssessmentConfig
              ?.assessmentSignatureUsers?.[0];
          const isProviderFirst =
            firstSigner &&
            (firstSigner.signatureUserType ===
              SignatureUser.SendingProviderUser ||
              firstSigner.signatureUserType ===
                SignatureUser.AssignedProviderUser);

          return isProviderFirst;
        },
      );

      const selectedPatientEntries = Object.values(patientSelections).filter(
        (entry) => entry.manual || entry.selectedTagIds.length > 0,
      );

      const programIds = Array.from(
        new Set(
          selectedPatientEntries.map(
            (entry) => entry.patient.programInstanceId,
          ),
        ),
      );

      if (!programIds.length) {
        customToast.error('Please select at least one client.');
        setSubmitting(false);
        return;
      }

      const programTagIds: string[] = [];

      const sendEmailValue = !hasProviderFirstSigner;

      const { data } = await sendFollowUpToManyMutation({
        variables: {
          input: {
            programIds,
            programTagIds,
            subject,
            providerNote,
            providerVoiceNoteId,
            activities: activities.map((activity) => {
              const activityInput = _.omit(
                activity.sendFollowUpActivityInputRaw,
                FollowUpDecoratorKeys,
              );

              return activityInput;
            }),
            sendEmail: sendEmailValue,
            sendSms: false,
            isIntake,
            ...(currentFollowUpTemplateDraftId.current &&
              followUpDrafts.some(
                (draft) => draft.id === currentFollowUpTemplateDraftId.current,
              ) && {
                sourceDraftFollowUpTemplateId:
                  currentFollowUpTemplateDraftId.current,
              }),
          },
        },
      });

      if (data?.sendFollowUpToMany) {
        if (hasProviderFirstSigner) {
          const allAssessmentIdsNeedingSignature: string[] = [];
          const assessmentIdToFollowUpIdMap: {
            [assessmentId: string]: string;
          } = {};
          const assessmentIdToProgramIdMap: { [assessmentId: string]: string } =
            {};
          const assessmentIdToPatientNameMap: {
            [assessmentId: string]: string;
          } = {};
          const allAssessmentIdsOrdered: string[] = [];
          const processedAssessmentIds = new Set<string>();

          for (const followUp of data.sendFollowUpToMany) {
            const patientName =
              patientsData?.patientNames?.find(
                (p: any) => p.programInstanceId === followUp.programId,
              )?.name || 'Client';

            const assessmentIds = getProgramAssessmentIdsNeedingSignature(
              followUp,
              authedProviderUser?.id,
            );

            if (assessmentIds && assessmentIds.length > 0) {
              assessmentIds.forEach((assessmentId) => {
                assessmentIdToFollowUpIdMap[assessmentId] = followUp.id;
                assessmentIdToProgramIdMap[assessmentId] = followUp.programId;

                if (!allAssessmentIdsOrdered.includes(assessmentId)) {
                  allAssessmentIdsOrdered.push(assessmentId);
                  assessmentIdToPatientNameMap[assessmentId] = patientName;
                }

                if (!processedAssessmentIds.has(assessmentId)) {
                  allAssessmentIdsNeedingSignature.push(assessmentId);
                  processedAssessmentIds.add(assessmentId);
                }
              });
            }
          }

          if (allAssessmentIdsNeedingSignature.length > 0) {
            const [
              firstProgramAssessmentId,
              ...remainingProgramAssessmentIdsNeedingSignature
            ] = allAssessmentIdsNeedingSignature;

            navigate(`/provider-form/${firstProgramAssessmentId}`, {
              state: {
                fromFollowUpBuilder: true,
                ...(remainingProgramAssessmentIdsNeedingSignature.length >
                  0 && {
                  remainingProgramAssessmentIdsNeedingSignature,
                }),
                assessmentIdToFollowUpIdMap,
                assessmentIdToProgramIdMap,
                allAssessmentIdsOrdered,
                assessmentIdToPatientNameMap,
              },
            });
          } else {
            if (onlyPatient) {
              navigate(`/clients/${onlyPatient.programInstanceId}/follow-ups`);
            } else {
              navigate(`/clients/active`);
            }
          }
        } else {
          customToast.success('Mail sent successfully.');

          if (onlyPatient) {
            const programAssessmentIdsNeedingSignature =
              getProgramAssessmentIdsNeedingSignature(
                data?.sendFollowUpToMany?.[0],
                authedProviderUser?.id,
              );

            if (programAssessmentIdsNeedingSignature?.length > 0) {
              const [
                firstProgramAssessmentId,
                ...remainingProgramAssessmentIdsNeedingSignature
              ] = programAssessmentIdsNeedingSignature;

              navigate(`/provider-form/${firstProgramAssessmentId}`, {
                state: {
                  remainingProgramAssessmentIdsNeedingSignature,
                },
              });
            } else {
              navigate(`/clients/${onlyPatient.programInstanceId}/follow-ups`);
            }
          } else {
            navigate(`/clients/active`);
          }
        }
      }
    } catch (err) {
      customToast.error('Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleErrors: SubmitErrorHandler<FollowUpForm> = (errors) => {
    // A validation error here means the practitioner submitted an incomplete
    // form; react-hook-form surfaces the field errors in the UI already. This
    // is expected user behavior, not a code defect, so record it as a
    // breadcrumb (warn) instead of a Sentry exception (WEB-A3).
    logger.warn('FollowUp submission rejected by form validation', { errors });
  };

  const sendFollowUpWithCheck = async (skipConfirmation = false) => {
    if (tagOrder.length > 0 && !skipConfirmation) {
      const uniquePatients = Object.values(patientSelections)
        .filter((entry) => entry.manual || entry.selectedTagIds.length > 0)
        .map((entry) => entry.patient);

      // Sort logic duplicated here or passed in?
      // Ideally we should use the sort function from useRecipientSelection but we don't have access to it here easily without passing it in.
      // For now, let's assume uniquePatients is enough or we can import the helper.
      // Actually, let's just use the list as is, or sort it if needed.
      // The original code used sortAndNormalizePatients.

      if (!uniquePatients.length) {
        customToast.error('Please select at least one client.');
        return;
      }

      setRecipientPatientNames(uniquePatients);
      setIsSendToTagConfirmationModalOpen(true);
    } else {
      const submitFn = handleSubmit(onSendFollowUp, handleErrors);
      await submitFn();
    }
  };

  // Function to refetch a specific patient and update patientsDataForModal
  const refetchPatientForModal = async (programId: string) => {
    try {
      const result = await fetchPatient({
        variables: { programId },
      });
      if (result.data?.patient) {
        setPatientsDataForModal((prev) => {
          const updated = [...prev];
          const index = updated.findIndex(
            (p) => p.programInstanceId === programId,
          );
          if (index >= 0) {
            updated[index] = result.data!.patient!;
          }
          return updated;
        });
      }
    } catch (err) {
      logger.error(
        err instanceof Error
          ? err
          : new Error('Error refetching patient for modal'),
        { programId },
      );
      customToast.error(
        'Failed to refresh client information. Please try again.',
      );
    }
  };

  return {
    submitting,
    sendingFollowUp,
    onSendFollowUp,
    sendFollowUpWithCheck,
    isSendToTagConfirmationModalOpen,
    setIsSendToTagConfirmationModalOpen,
    recipientPatientNames,
    isReviewSignerDetailsModalOpen,
    setIsReviewSignerDetailsModalOpen,
    unassignedMultiSignatureActivities,
    patientsDataForModal,
    refetchPatientForModal,
    handleErrors,
  };
};
